import type { TFunction } from 'i18next';
import type { UseFormReturn } from 'react-hook-form';
import { BadgeInfo } from 'lucide-react';
import type { WaitingListWaitingListWriteModelRole } from '@r/api/generated/client.schemas.ts';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { CountryDropdown } from '@ui/country-dropdown.tsx';
import type { ApiCountry } from '@ui/country-dropdown.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import {
  acceptMissingFieldLabel,
  isAcceptMandatoryField
} from '@admin/components/company/waiting-list/waiting-list-edit-modal-helpers.ts';
import type { WaitingListEditFormValues } from '@admin/components/company/waiting-list/waiting-list-edit-form-schema.ts';

export function AcceptRequiredMark() {
  return (
    <span className="text-warning" aria-hidden>
      *
    </span>
  );
}

export function AcceptNoticeBlock({
  t,
  acceptMissingFields
}: {
  t: TFunction;
  acceptMissingFields: (keyof WaitingListEditFormValues)[];
}) {
  if (acceptMissingFields.length === 0) return null;
  return (
    <div className="flex w-full items-center justify-start gap-1.5 text-sm text-warning">
      <span className="leading-snug">{t('admin.waiting.list.edit.cta.accept.notice.lead')}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="shrink-0 rounded-sm text-warning hover:text-warning/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('admin.waiting.list.edit.cta.accept.notice.info.aria')}
            >
              <BadgeInfo className="h-4 w-4 stroke-warning" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-2">
              <p className="font-medium">
                {t('admin.waiting.list.edit.cta.accept.disabled.tooltip')}
              </p>
              <ul className="list-disc space-y-1 pl-4">
                {acceptMissingFields.map((field) => (
                  <li key={field}>{acceptMissingFieldLabel(field, t)}</li>
                ))}
              </ul>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export function WaitingListEditField({
  form,
  name,
  label,
  type,
  className,
  acceptRequired
}: {
  form: UseFormReturn<WaitingListEditFormValues>;
  name: keyof WaitingListEditFormValues;
  label: string;
  type?: string;
  className?: string;
  acceptRequired?: boolean;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel className="inline-flex items-center gap-0.5">
            {label}
            {acceptRequired ? <AcceptRequiredMark /> : null}
          </FormLabel>
          <FormControl>
            <Input {...field} type={type} value={(field.value as string | undefined) ?? ''} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function WaitingListEditCompanyFields({
  form,
  countries,
  isCountriesLoading,
  typologyConversionMode,
  typologies,
  typologiesLoading,
  showAcceptRequiredStars,
  t
}: {
  form: UseFormReturn<WaitingListEditFormValues>;
  countries: ApiCountry[];
  isCountriesLoading: boolean;
  typologyConversionMode: boolean;
  typologies: Array<{ id: number; name: string }>;
  typologiesLoading: boolean;
  showAcceptRequiredStars: boolean;
  t: TFunction;
}) {
  const countryAlpha3 = form.watch('countryAlpha3');

  return (
    <div className="flex flex-col gap-4">
      <WaitingListEditField
        form={form}
        name="companyName"
        label={t('label.company.name')}
        acceptRequired={
          isAcceptMandatoryField('companyName', typologyConversionMode) && showAcceptRequiredStars
        }
      />
      <WaitingListEditField
        form={form}
        name="companyUniqueCode"
        label={t('company.create.unique.code.label')}
        acceptRequired={
          isAcceptMandatoryField('companyUniqueCode', typologyConversionMode) &&
          showAcceptRequiredStars
        }
      />
      <WaitingListEditField form={form} name="tva" label={t('label.company.tva')} />
      <WaitingListEditField form={form} name="siren" label={t('label.company.siren')} />
      <WaitingListEditField
        form={form}
        name="addressLine1"
        label={t('label.company.address.line1')}
        acceptRequired={
          isAcceptMandatoryField('addressLine1', typologyConversionMode) && showAcceptRequiredStars
        }
      />
      <WaitingListEditField
        form={form}
        name="addressLine2"
        label={t('label.company.address.line.2')}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-start">
        <WaitingListEditField
          className="md:col-span-2"
          form={form}
          name="zipcode"
          label={t('label.company.zipcode')}
          acceptRequired={
            isAcceptMandatoryField('zipcode', typologyConversionMode) && showAcceptRequiredStars
          }
        />
        <WaitingListEditField
          className="md:col-span-8"
          form={form}
          name="city"
          label={t('label.company.city')}
          acceptRequired={
            isAcceptMandatoryField('city', typologyConversionMode) && showAcceptRequiredStars
          }
        />
        <FormField
          control={form.control}
          name="countryAlpha3"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="inline-flex items-center gap-0.5">
                {t('label.company.country')}
                {isAcceptMandatoryField('countryAlpha3', typologyConversionMode) &&
                showAcceptRequiredStars ? (
                  <AcceptRequiredMark />
                ) : null}
              </FormLabel>
              <CountryDropdown
                placeholder={t('label.company.country')}
                defaultValue={countryAlpha3}
                loading={isCountriesLoading}
                options={countries}
                slim
                onChange={(country: ApiCountry) => field.onChange(country.alpha3)}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {typologyConversionMode ? (
        <FormField
          control={form.control}
          name="typology"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-flex items-center gap-0.5">
                {t('company.create.typology.name.label')}
                {isAcceptMandatoryField('typology', typologyConversionMode) &&
                showAcceptRequiredStars ? (
                  <AcceptRequiredMark />
                ) : null}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || undefined}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('company.create.typology.name.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {typologiesLoading ? (
                    <SelectItem value="__loading" disabled>
                      {t('company.create.typology.name.loading')}
                    </SelectItem>
                  ) : (
                    typologies.map((typology) => (
                      <SelectItem key={typology.id} value={`/admin/typologies/${typology.id}`}>
                        {typology.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <FormField
          control={form.control}
          name="pointCoeff"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-flex items-center gap-0.5">
                {t('label.company.point.coefficient')}
                {isAcceptMandatoryField('pointCoeff', typologyConversionMode) &&
                showAcceptRequiredStars ? (
                  <AcceptRequiredMark />
                ) : null}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={
                    field.value === undefined || field.value === null ? '' : String(field.value)
                  }
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (v === '') {
                      field.onChange(undefined);
                      return;
                    }
                    const n = Number(v.replace(',', '.'));
                    field.onChange(Number.isNaN(n) ? undefined : n);
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

export function WaitingListEditManagerFields({
  form,
  roleOptions,
  typologyConversionMode,
  showAcceptRequiredStars,
  t
}: {
  form: UseFormReturn<WaitingListEditFormValues>;
  roleOptions: { value: WaitingListWaitingListWriteModelRole; label: string }[];
  typologyConversionMode: boolean;
  showAcceptRequiredStars: boolean;
  t: TFunction;
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      <WaitingListEditField
        form={form}
        name="email"
        label={t('label.user.email')}
        type="email"
        className="w-full"
        acceptRequired={
          isAcceptMandatoryField('email', typologyConversionMode) && showAcceptRequiredStars
        }
      />
      <WaitingListEditField
        form={form}
        name="firstname"
        label={t('admin.waiting.list.edit.label.user.firstname')}
        className="w-full"
        acceptRequired={
          isAcceptMandatoryField('firstname', typologyConversionMode) && showAcceptRequiredStars
        }
      />
      <WaitingListEditField
        form={form}
        name="lastname"
        label={t('admin.waiting.list.edit.label.user.lastname')}
        className="w-full"
        acceptRequired={
          isAcceptMandatoryField('lastname', typologyConversionMode) && showAcceptRequiredStars
        }
      />
      <WaitingListEditField
        form={form}
        name="userUniqueCode"
        label={t('label.user.unique.code')}
        className="w-full"
        acceptRequired={
          isAcceptMandatoryField('userUniqueCode', typologyConversionMode) &&
          showAcceptRequiredStars
        }
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-flex items-center gap-0.5">
                {t('label.user.role')}
                {isAcceptMandatoryField('role', typologyConversionMode) &&
                showAcceptRequiredStars ? (
                  <AcceptRequiredMark />
                ) : null}
              </FormLabel>
              <Select
                onValueChange={field.onChange}
                value={
                  field.value === null || field.value === undefined || field.value === ''
                    ? undefined
                    : field.value
                }
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('label.user.role')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {roleOptions.map((opt) => (
                    <SelectItem key={opt.value ?? ''} value={opt.value ?? ''}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="comment"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('label.comment')}</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={4}
                className="min-h-[100px] resize-y"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
